import type { RecordData } from '@tomo/core';
import { Button, toast } from '@tomo/ui';
import { Download, ExternalLink, ImagePlus, RefreshCw, X } from 'lucide-react';
import { useState } from 'react';
import { getBlob, ingestFile, useBlobUrl } from '../../data/blobs.ts';
import { useProject, useSession } from '../../data/hooks.tsx';
import { downloadBlob, formatBytes, pickFiles } from '../../lib/files.ts';
import './assets.css';

/**
 * The picture a record carries without any field for it, shown above the record. Every record can
 * have one, so a gallery of anything works without setting a field up first.
 */
export function RecordCover({ record, readOnly }: { record: RecordData; readOnly?: boolean }) {
  const project = useProject();
  const session = useSession();
  const cover = record.cover;
  const url = useBlobUrl(cover?.hash ?? null, session.id);
  const [busy, setBusy] = useState(false);

  const choose = async () => {
    const [f] = await pickFiles({ multiple: false, accept: 'image/*' });
    if (!f) return;
    setBusy(true);
    try {
      project.setCover(record.id, await ingestFile(f, f.name));
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    if (!cover) return;
    const blob = await getBlob(cover.hash);
    if (blob) downloadBlob(blob, cover.name);
    else toast.error('The picture is not available on this device yet.');
  };

  if (!cover) {
    if (readOnly) return null;
    return (
      <Button size="sm" variant="ghost" icon={<ImagePlus />} loading={busy} onClick={choose} className="record-cover__add">
        Add a cover
      </Button>
    );
  }

  return (
    <div className="asset-preview">
      <div className="asset-preview__stage" data-kind="image">
        {url ? <img src={url} alt={record.title} /> : <div className="asset-thumb__loading" />}
      </div>
      <div className="asset-preview__bar">
        <span className="asset-preview__name g-truncate">{cover.name}</span>
        <span className="asset-preview__facts tabular">
          {[cover.mime.split('/')[1]?.toUpperCase(), formatBytes(cover.size), cover.width ? `${cover.width}×${cover.height}` : null].filter(Boolean).join(' · ')}
        </span>
        <span className="g-spacer" />
        {url && (
          <Button size="sm" variant="ghost" icon={<ExternalLink />} onClick={() => window.open(url, '_blank', 'noopener')}>
            Full size
          </Button>
        )}
        <Button size="sm" variant="ghost" icon={<Download />} onClick={download}>
          Download
        </Button>
        {!readOnly && (
          <>
            <Button size="sm" variant="ghost" icon={<RefreshCw />} loading={busy} onClick={choose}>
              Replace
            </Button>
            <Button size="sm" variant="ghost" icon={<X />} onClick={() => project.setCover(record.id, null)}>
              Remove
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
